import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Loader2 } from "lucide-react";
import closeIcon from "../../assets/cross.svg";

/**
 * Confirmation dialog for revoking an issued certificate.
 *
 * Revoking is a soft state change — the cert remains in the DB and the
 * public verify URL keeps resolving (with a red "Revoked" banner). The
 * reason field is optional but encouraged because it surfaces verbatim to
 * external viewers (employers / scholarship reviewers).
 */
export default function RevokeCertificateModal({ cert, onClose, onConfirm }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleConfirm = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await onConfirm(reason.trim());
    } catch (err) {
      setError(err.message || "Failed to revoke");
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-center items-center p-4">
      <div className="w-[480px] max-w-full bg-white rounded-[20px] overflow-hidden font-['Manrope']">
        <div className="p-6 bg-gradient-to-br from-white to-red-50 border-b border-red-100 flex justify-between items-start">
          <div className="flex gap-3">
            <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-full bg-red-100 text-red-600">
              <AlertTriangle size={18} />
            </div>
            <div>
              <h2 className="text-[18px] font-bold text-gray-900">
                {t("certificates.revokeModal.title", { defaultValue: "Revoke this certificate?" })}
              </h2>
              <p className="text-sm text-gray-600 mt-0.5">
                {t("certificates.revokeModal.subtitle", {
                  defaultValue: "External verify pages will show the revocation. Cannot be undone from this UI.",
                })}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1" type="button" disabled={submitting}>
            <img src={closeIcon} alt="Close" className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 flex flex-col gap-4">
          <div className="rounded-[10px] bg-gray-50 border border-gray-200 px-3 py-2 text-sm">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t("certificates.revokeModal.certificate", { defaultValue: "Certificate" })}
            </div>
            <div className="mt-0.5 text-gray-900 font-medium">{cert?.title}</div>
            <div className="text-xs text-gray-500 mt-0.5">
              {t("certificates.revokeModal.issuedTo", {
                defaultValue: "Issued to {{name}}",
                name: cert?.studentDisplayName || "—",
              })}
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold text-gray-700 mb-2 block">
              {t("certificates.revokeModal.reasonLabel", { defaultValue: "Reason (shown publicly)" })}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={400}
              rows={3}
              placeholder={t("certificates.revokeModal.reasonPlaceholder", {
                defaultValue: "e.g. Issued in error — replaced by corrected certificate.",
              })}
              className="w-full px-3 py-2.5 rounded-[10px] border border-gray-300 text-sm focus:outline-none focus:border-red-400 resize-y"
            />
            <p className="text-xs text-gray-400 mt-1">
              {t("certificates.revokeModal.reasonHelp", {
                defaultValue: "Optional but recommended. Anyone scanning the cert's QR will see this.",
              })}
            </p>
          </div>

          {error && (
            <div className="rounded-[10px] bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        <div className="px-6 pb-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-5 py-2.5 rounded-[10px] border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {t("certificates.revokeModal.cancel", { defaultValue: "Keep certificate" })}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[10px] bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {submitting
              ? t("certificates.revokeModal.revoking", { defaultValue: "Revoking…" })
              : t("certificates.revokeModal.confirm", { defaultValue: "Revoke certificate" })}
          </button>
        </div>
      </div>
    </div>
  );
}
